import { JsonObject } from '../../app/json/jsonValue.js';
import { AppSiteResult } from './appSiteResult.js';

/**
 * Interface for dynamic AppSite handlers that resolve which AppSite/AppView is actually
 * rendered for a request, based on the requested AppSite/AppView and the current state.
 *
 * Unlike IView/IData/IAction (which are component-triggered and run inside the per-component
 * merge), an IAppSite handler is AppSite-triggered: it runs once at the very start of the merge
 * and may redefine the AppSite/AppView before any template/appfile resolution occurs.
 *
 * Responsibilities:
 * - Expose handler identity for diagnostics and operation tracking
 * - Accept the requested AppSite/AppView plus parameters and request state
 * - Return the AppSite/AppView to render (unchanged when no redefinition applies)
 */
export abstract class IAppSite {
    /**
     * Gets the registry type name used for dynamic-appsite tracking (e.g., "ReplaceAppSite").
     */
    abstract get typeName(): string;

    /**
     * Resolves the AppSite/AppView to render for the requested AppSite/AppView.
     * Returns the requested values unchanged when no redefinition applies.
     *
     * @param appSite The requested application site name
     * @param appView The requested application view name (may be empty)
     * @param paramJson Parameter JSON from the DynamicAppSite.*.json file (drives the resolution)
     * @param contextJson Request/merge context (carries state such as IsBootstrapMode/IsAuthenticated)
     * @param configJson Optional request config JSON (handler decides how to use)
     * @returns The AppSite/AppView to render, as an AppSiteResult
     */
    abstract resolveAppSite(
        appSite: string,
        appView: string,
        paramJson: JsonObject,
        contextJson: JsonObject,
        configJson: JsonObject | null
    ): AppSiteResult;
}
